import { useCallback, useMemo } from "react";
import { useLocalStorage } from "./useLocalStorage";

export const REVIEW_BOARD_MIN_WIDTH = 220;
export const REVIEW_BOARD_MAX_WIDTH = 720;

const DEFAULT_LAYOUT = { order: [], widths: {}, hidden: [] };

/**
 * Persisted layout state for the Review Board.
 *
 * Stores, per browser (localStorage):
 *  - order:  array of column ids in display order
 *  - widths: map of column id -> pixel width (when expanded)
 *  - hidden: array of column ids the user has removed from the board
 *
 * The persisted state is reconciled against the canonical set of columns
 * (`columnDefs`) on every read so that newly added column types appear and
 * removed ones drop out without corrupting saved preferences.
 *
 * @param {Array<{id: string, defaultWidth: number}>} columnDefs
 */
export const useReviewBoardLayout = (columnDefs) => {
	const [layout, setLayout] = useLocalStorage(
		"reviewboard-layout",
		DEFAULT_LAYOUT,
	);

	const allIds = useMemo(() => columnDefs.map((c) => c.id), [columnDefs]);

	const defaultWidthFor = useCallback(
		(id) => columnDefs.find((c) => c.id === id)?.defaultWidth ?? 360,
		[columnDefs],
	);

	// Persisted order, filtered to known ids, with any brand-new ids appended.
	const order = useMemo(() => {
		const known = (layout.order || []).filter((id) => allIds.includes(id));
		const missing = allIds.filter((id) => !known.includes(id));
		return [...known, ...missing];
	}, [layout.order, allIds]);

	const hidden = useMemo(
		() => (layout.hidden || []).filter((id) => allIds.includes(id)),
		[layout.hidden, allIds],
	);

	const widthFor = useCallback(
		(id) => layout.widths?.[id] ?? defaultWidthFor(id),
		[layout.widths, defaultWidthFor],
	);

	const setColumnWidth = useCallback(
		(id, width) => {
			const clamped = Math.max(
				REVIEW_BOARD_MIN_WIDTH,
				Math.min(REVIEW_BOARD_MAX_WIDTH, Math.round(width)),
			);
			setLayout((prev) => ({
				...prev,
				widths: { ...(prev.widths || {}), [id]: clamped },
			}));
		},
		[setLayout],
	);

	const hideColumn = useCallback(
		(id) => {
			setLayout((prev) => {
				const prevHidden = prev.hidden || [];
				if (prevHidden.includes(id)) return prev;
				return { ...prev, hidden: [...prevHidden, id] };
			});
		},
		[setLayout],
	);

	const showColumn = useCallback(
		(id) => {
			setLayout((prev) => ({
				...prev,
				hidden: (prev.hidden || []).filter((h) => h !== id),
			}));
		},
		[setLayout],
	);

	// Move `dragId` relative to `targetId`. `edge` ("before" | "after")
	// controls which side of the target the column is inserted on.
	const moveColumn = useCallback(
		(dragId, targetId, edge = "before") => {
			if (!dragId || dragId === targetId) return;
			setLayout((prev) => {
				const base = (prev.order || []).filter((id) => allIds.includes(id));
				for (const id of allIds) {
					if (!base.includes(id)) base.push(id);
				}
				const without = base.filter((id) => id !== dragId);
				let idx = without.indexOf(targetId);
				if (idx === -1) {
					without.push(dragId);
				} else {
					if (edge === "after") idx += 1;
					without.splice(idx, 0, dragId);
				}
				return { ...prev, order: without };
			});
		},
		[setLayout, allIds],
	);

	const resetLayout = useCallback(() => {
		setLayout(DEFAULT_LAYOUT);
	}, [setLayout]);

	return {
		order,
		hidden,
		widthFor,
		setColumnWidth,
		hideColumn,
		showColumn,
		moveColumn,
		resetLayout,
	};
};
